import { useEffect, useRef, useState } from "react";
import { isLocalId, outbox } from "./index";

/**
 * A page showing a record created offline ("local-…") moves to the record's real address once
 * it is synced: onResolved(serverId) runs when the sync happens (or already happened). While
 * `when` is false (unsaved edits on screen) the move waits.
 */
export const useSyncedRecord = (id, onResolved, { when = true } = {}) => {
  const [serverId, setServerId] = useState(null);
  const callback = useRef(onResolved);
  callback.current = onResolved;

  useEffect(() => {
    setServerId(null);
    if (!isLocalId(id)) return undefined;
    let active = true;
    outbox.resolveId(id).then(resolved => {
      if (active && resolved !== id) setServerId(resolved);
    });
    const stop = outbox.onSynced(item => {
      if (item.localId === id)
        outbox.resolveId(id).then(resolved => active && setServerId(resolved));
    });
    return () => {
      active = false;
      stop();
    };
  }, [id]);

  useEffect(() => {
    if (serverId && !isLocalId(serverId) && when) callback.current(serverId);
  }, [serverId, when]);
};

/** Runs `callback` after a queued change of `module` reached the server (e.g. to reload). */
export const useOnSynced = (module, callback) => {
  const latest = useRef(callback);
  latest.current = callback;
  useEffect(
    () =>
      outbox.onSynced(item => {
        if (item.module === module) latest.current(item);
      }),
    [module]
  );
};
